import { CircleAlert, CircleCheck, Info, TriangleAlert, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { TONE_SUBTLE } from './Badge'
import { Button } from './Button'
import type { ActionDef, Tone } from './types'

export interface BannerProps {
  tone?: Tone
  title?: string
  children?: ReactNode
  icon?: LucideIcon
  /** höchstens eine Aktion */
  action?: ActionDef
  /** randlos über die volle Breite (unter der Topbar) statt als Karte im Seitenfluss */
  flush?: boolean
  className?: string
  'data-testid'?: string
}

const ICON: Record<Tone, LucideIcon> = {
  neutral: Info,
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  danger: CircleAlert,
}
const ICON_TONE: Record<Tone, string> = {
  neutral: 'text-fg-muted',
  info: 'text-info-fg',
  success: 'text-success-fg',
  warning: 'text-warning-fg',
  danger: 'text-danger-fg',
}

/** Hinweis im Seitenfluss (Mindestbestellwert nicht erreicht, offline) – mit Symbol und höchstens einer Aktion. */
export function Banner({ tone = 'info', title, children, icon, action, flush, className, 'data-testid': testId }: BannerProps) {
  const Icon = icon ?? ICON[tone]
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      data-testid={testId}
      data-ui="card"
      className={cn(
        'flex items-start gap-3 px-4 py-3 text-body',
        flush ? 'border-b' : 'rounded-md border',
        TONE_SUBTLE[tone],
        'text-fg',
        className,
      )}
    >
      <Icon className={cn('mt-0.5 size-4 shrink-0', ICON_TONE[tone])} strokeWidth={1.75} aria-hidden />
      <div className="min-w-0 flex-1">
        {title && <p className="font-medium text-fg">{title}</p>}
        {children && <div className="text-dense text-fg-muted">{children}</div>}
      </div>
      {action && (
        <Button
          size="sm"
          variant="secondary"
          icon={action.icon}
          onClick={() => void action.onSelect()}
          disabled={action.disabled}
          disabledReason={action.disabledReason}
        >
          {action.label}
        </Button>
      )}
    </div>
  )
}
